/* global gdAlertify */
import React, { useState } from "react";
import axiosInstance from "../config/axiosConfig";
import BuscarPaciente from "./components/BuscarPaciente";
import { formatDate } from "../utils/utils";
import {
    getNoEnviarPacienteHistory,
    getNoEnviarPacienteStatus,
    isNoEnviarRecordActive,
} from "../utils/noEnviarPaciente";

const ResumenPacientesCoordinacion = () => {
    const [pacienteInfo, setPacienteInfo] = useState(null);
    const [resumen, setResumen] = useState([]);
    const [anio, setAnio] = useState(new Date().getFullYear());
    const [auxiliarSeleccionado, setAuxiliarSeleccionado] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSeleccionaPaciente = (paciente) => {
        if (!paciente) {
            setPacienteInfo(null);
            setResumen([]);
            setAuxiliarSeleccionado(null);
            return;
        }
        setPacienteInfo(paciente);
        // Búsqueda automática al seleccionar
        buscar(paciente.id);
    };

    const handleAnioChange = (e) => {
        setAnio(e.target.value);
    };

    const buscar = async (pid = null) => {
        const idToSearch = pid || pacienteInfo?.id;
        if (!idToSearch) {
            gdAlertify.notify('Seleccione paciente', 'error');
            return;
        }
        
        setLoading(true);
        gdAlertify.showLoader();
        
        try {
            const response = await axiosInstance.post("/paciente/resumen-coordinacion", {
                pacienteID: idToSearch,
                anio: anio
            });
            if (!response.data.error) {
                setResumen(response.data.data || []);
                setAuxiliarSeleccionado(null);
            } else {
                gdAlertify.notify(response.data.message || 'Error al obtener resumen', 'error');
            }
        } catch (error) {
            console.error("Error al obtener los datos: ", error);
            gdAlertify.notify('Fallo interno al consultar', 'error');
        } finally {
            setLoading(false);
            gdAlertify.hideLoader();
        }
    };

    const verAuxiliar = (auxiliar) => {
        setAuxiliarSeleccionado(auxiliar);
    };

    const getPeriodMonth = (dateStr) => {
        const date = new Date(dateStr + 'T12:00:00');
        const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        const day = date.getDate();
        let monthIndex = date.getMonth();
        let year = date.getFullYear();

        if (day >= 26) {
            monthIndex = (monthIndex + 1) % 12;
            if (monthIndex === 0) year++;
        }

        return `${months[monthIndex]} ${year}`;
    };

    const getFullDateWithDay = (dateStr) => {
        if (!dateStr) return "";
        const days = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
        
        // Parsing robusto para YYYY-MM-DD o DD-MM-YYYY
        let dateObj;
        if (dateStr.includes("-")) {
            const parts = dateStr.split(' ')[0].split('-');
            if (parts[0].length === 4) { // YYYY-MM-DD
                dateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
            } else { // DD-MM-YYYY
                dateObj = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]), 12, 0, 0);
            }
        } else {
            dateObj = new Date(dateStr + 'T12:00:00');
        }

        const dayIndex = dateObj.getDay();
        const dayName = isNaN(dayIndex) ? "" : days[dayIndex];
        return `${dayName} ${formatDate(dateStr) || dateStr}`.trim();
    };

    const groupDetalleByPeriod = (detalle) => {
        if (!detalle) return {};
        const groups = {};
        detalle.forEach(item => {
            const period = getPeriodMonth(item.date);
            if (!groups[period]) groups[period] = [];
            groups[period].push(item);
        });
        return groups;
    };

    const groupedDetalle = auxiliarSeleccionado ? groupDetalleByPeriod(auxiliarSeleccionado.detalle) : {};
    const observacionesHistoricas = auxiliarSeleccionado?.bitacora || [];
    const noEnviarStatus = auxiliarSeleccionado ? getNoEnviarPacienteStatus(auxiliarSeleccionado) : { active: false };
    const noEnviarHistorialRaw = auxiliarSeleccionado ? getNoEnviarPacienteHistory(auxiliarSeleccionado) : [];
    const noEnviarHistorial = noEnviarHistorialRaw.length > 0
        ? noEnviarHistorialRaw
        : (noEnviarStatus.active || noEnviarStatus.observation
            ? [{
                fecha: noEnviarStatus.date,
                observacion: noEnviarStatus.observation,
                activo: noEnviarStatus.active ? 1 : 0,
            }]
            : []);

    return (
        <div className="container-fluid py-3 bg-light min-vh-100 font-inter">
            {/* ENCABEZADO Y FILTROS COMPACTOS */}
            <div className="card shadow-sm border-0 mb-3 rounded-3" style={{ zIndex: 10 }}>
                <div className="card-body p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary-subtle p-2 rounded-3 me-3">
                            <i className="bi bi-calendar-check fs-4 text-primary"></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">Análisis de Asistencia y Bitácora</h5>
                            <span className="small text-muted italic">Gestión de turnos y observaciones históricas</span>
                        </div>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <div style={{ width: '380px' }}>
                            <BuscarPaciente onSelect={handleSeleccionaPaciente} onClear={handleSeleccionaPaciente} conPaciente={true} hideLabel={true} />
                        </div>
                        <select className="form-select form-select-sm shadow-none w-auto py-2" value={anio} onChange={handleAnioChange}>
                            {[2023, 2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <button className="btn btn-primary btn-sm px-4 fw-bold shadow-none py-2" onClick={() => buscar()} disabled={loading}>
                            {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-arrow-repeat me-2"></i>}
                            Actualizar
                        </button>
                    </div>
                </div>
            </div>

            {resumen.length > 0 && (
                <div className="row g-3">
                    {/* COLUMNA IZQUIERDA: LISTA Y OBSERVACIONES */}
                    <div className="col-md-3">
                        {/* CARD 1: AUXILIARES */}
                        <div className="card shadow-sm border-0 mb-3 rounded-3 overflow-hidden">
                            <div className="card-header bg-white border-bottom py-2 px-3">
                                <span className="small text-muted fw-bold text-uppercase">Auxiliares en {anio}</span>
                            </div>
                            <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {resumen.map((aux, idx) => (
                                    <button
                                        key={idx}
                                        className={`list-group-item list-group-item-action py-2 px-3 border-0 d-flex justify-content-between align-items-center ${auxiliarSeleccionado?.auxiliar_id === aux.auxiliar_id ? 'bg-primary text-white shadow-sm' : ''}`}
                                        onClick={() => verAuxiliar(aux)}
                                    >
                                        <div className="text-truncate" style={{ fontSize: '0.88rem' }}>
                                            <span className="fw-semibold">{aux.nombre} {aux.apellidoPaterno}</span>
                                            {getNoEnviarPacienteStatus(aux).active && (
                                                <span className="badge bg-warning text-dark ms-2">No enviar</span>
                                            )}
                                        </div>
                                        <span className={`badge rounded-pill ${auxiliarSeleccionado?.auxiliar_id === aux.auxiliar_id ? 'bg-white text-primary' : 'bg-primary-subtle text-primary border border-primary-subtle'}`}>
                                            {aux.cantidad_visitas}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* CARD 2: OBSERVACIONES CRONOLÓGICAS DEL AUXILIAR (HISTÓRICAS) */}
                        <div className="card shadow-sm border-0 mb-3 rounded-3 overflow-hidden">
                            <div className="card-header bg-warning-subtle border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
                                <span className="small text-warning-emphasis fw-bold text-uppercase">
                                    <i className="bi bi-exclamation-triangle me-1"></i>No Enviar a Este Domicilio
                                </span>
                                <span className={`badge ${noEnviarStatus.active ? 'bg-warning text-dark' : 'bg-light text-muted border'}`}>
                                    {noEnviarStatus.active ? 'Activo' : 'Sin marca'}
                                </span>
                            </div>
                            <div className="p-0 overflow-auto" style={{ maxHeight: '220px', backgroundColor: '#fffdf5' }}>
                                {auxiliarSeleccionado ? (
                                    noEnviarHistorial.length > 0 ? (
                                        <div className="p-2 d-flex flex-column gap-2">
                                            {noEnviarHistorial.map((item, idx) => (
                                                <div key={idx} className="bg-white border rounded p-2 shadow-xs border-warning-subtle">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <span className="fw-bold text-warning-emphasis" style={{ fontSize: '0.75rem' }}>
                                                            {item.fecha || item.date || item.fecha_registro
                                                                ? getFullDateWithDay(item.fecha || item.date || item.fecha_registro)
                                                                : 'Sin fecha'}
                                                        </span>
                                                        <span className={`badge ${isNoEnviarRecordActive(item) ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                            {isNoEnviarRecordActive(item) ? 'Activo' : 'Eliminado'}
                                                        </span>
                                                    </div>
                                                    <p className="mb-0 text-dark" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                                        {item.observacion || item.observation || 'Sin observacion'}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-4 text-center text-muted small italic">No hay marcas registradas para este auxiliar.</div>
                                    )
                                ) : (
                                    <div className="p-4 text-center text-muted small italic">Seleccione un auxiliar a visualizar.</div>
                                )}
                            </div>
                        </div>

                        <div className="card shadow-sm border-0 rounded-3 overflow-hidden">
                            <div className="card-header bg-primary-subtle border-bottom py-2 px-3 d-flex justify-content-between align-items-center">
                                <span className="small text-primary fw-bold text-uppercase"><i className="bi bi-clock-history me-1"></i>Bitácora Histórica</span>
                                <span className="badge bg-primary rounded-pill">{observacionesHistoricas.length}</span>
                            </div>
                            <div className="p-0 overflow-auto" style={{ maxHeight: '480px', backgroundColor: '#fcfcfc' }}>
                                {auxiliarSeleccionado ? (
                                    observacionesHistoricas.length > 0 ? (
                                        <div className="p-2 d-flex flex-column gap-2">
                                            {observacionesHistoricas.map((obs, oIdx) => (
                                                <div key={oIdx} className="bg-white border rounded p-2 shadow-xs border-light-subtle">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <span className="fw-bold text-primary" style={{ fontSize: '0.75rem' }}>{getFullDateWithDay(obs.date)}</span>
                                                        {obs.period && (
                                                            <span className={`badge rounded-pill italic ${obs.period === 'day' ? 'text-primary border-primary border' : 'text-danger border-danger border'}`} style={{fontSize: '0.65rem'}}>
                                                                {obs.period === 'day' ? 'Día' : 'Noche'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="mb-0 text-dark" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>{obs.observacion}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-4 text-center text-muted small italic">No hay observaciones históricas registradas.</div>
                                    )
                                ) : (
                                    <div className="p-4 text-center text-muted small italic">Seleccione un auxiliar a visualizar.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* COLUMNA DERECHA: CRONOGRAMA DETALLADO */}
                    <div className="col-md-9">
                        {auxiliarSeleccionado ? (
                            <div className="card shadow-sm border-0 rounded-3 overflow-hidden h-100 bg-white">
                                <div className="card-header bg-white border-bottom py-3 px-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0 fw-bold">
                                            Timeline de Asistencia {anio}: <span className="text-primary">{auxiliarSeleccionado.nombre} {auxiliarSeleccionado.apellidoPaterno}</span>
                                            {noEnviarStatus.active && (
                                                <span className="badge bg-warning text-dark ms-2">NO ENVIAR A ESTE DOMICILIO</span>
                                            )}
                                        </h6>
                                        <div className="text-muted small">Total del periodo: <b>{auxiliarSeleccionado.cantidad_visitas}</b> turnos</div>
                                    </div>
                                </div>
                                <div className="card-body p-0 overflow-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                                    {Object.keys(groupedDetalle).map(period => (
                                        <div key={period} className="assistant-section">
                                            <div className="bg-light px-4 py-1 border-bottom text-primary fw-bold small text-uppercase sticky-top" style={{ zIndex: 5, top: 0 }}>
                                                {period}
                                            </div>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                                                    <thead className="bg-white sticky-top shadow-xs" style={{ top: '30px', zIndex: 4 }}>
                                                        <tr className="text-muted small">
                                                            <th style={{ width: '30%' }} className="ps-4">Fecha y Turno</th>
                                                            <th style={{ width: '70%' }} className="pe-4">Notas de Gestión / Oficina</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {groupedDetalle[period].map((d, i) => (
                                                            <tr key={i} className="border-bottom-0">
                                                                <td className="ps-4 py-2 align-middle">
                                                                    <div className="d-flex flex-column">
                                                                        <span className="fw-semibold text-dark" style={{fontSize: '0.85rem'}}>{getFullDateWithDay(d.date)}</span>
                                                                        <div className="mt-1">
                                                                            <span className={`badge ${d.period === 'day' ? 'bg-primary' : 'bg-danger'} py-1 px-2`} style={{ fontSize: '0.62rem', opacity: 0.85 }}>
                                                                                {d.period === 'day' ? 'TURNO DÍA' : 'TURNO NOCHE'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="pe-4 py-2">
                                                                    <div className="d-flex flex-column gap-1">
                                                                        {d.notes && <div className="p-2 border border-warning-subtle rounded-3 bg-warning-subtle text-dark shadow-xs" style={{ fontSize: '0.8rem' }}><i className="bi bi-clipboard-check me-2 opacity-50"></i>{d.notes}</div>}
                                                                        {d.notas_internas && <div className="p-2 border border-info-subtle rounded-3 bg-primary-subtle text-dark shadow-xs" style={{ fontSize: '0.8rem' }}><i className="bi bi-lock me-2 opacity-50"></i>{d.notas_internas}</div>}
                                                                        {!d.notes && !d.notas_internas && <span className="text-muted small italic opacity-30">Sin notas de gestión registradas</span>}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column justify-content-center align-items-center h-100 bg-white shadow-sm rounded-3 py-5">
                                <div className="bg-light rounded-circle p-4 mb-3">
                                    <i className="bi bi-file-earmark-person fs-1 text-muted opacity-25"></i>
                                </div>
                                <h6 className="text-muted">Seleccione un auxiliar para cargar su historial detallado</h6>
                                <p className="small text-muted opacity-75">Los datos se agruparán por periodos mensuales automáticamente.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!resumen.length && pacienteInfo && (
                <div className="text-center py-5">
                    <div className="bg-white d-inline-block p-5 rounded-4 shadow-sm">
                        <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
                        <h6 className="text-muted mb-0">No se encontraron registros en {anio} para el paciente seleccionado.</h6>
                    </div>
                </div>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                .font-inter { font-family: 'Inter', sans-serif; }
                .shadow-xs { box-shadow: 0 1px 2px 0 rgba(0,0,0,0.05); }
                .bg-light-subtle { background-color: #f8f9fa; }
                .italic { font-style: italic; }
                .assistant-section:last-child { margin-bottom: 20px; }
                ::-webkit-scrollbar { width: 6px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 10px; }
                ::-webkit-scrollbar-thumb:hover { background: #d0d0d0; }
                .table-hover tbody tr:hover { background-color: #fdfdfd; }
            `}</style>
        </div>
    );
};

export default ResumenPacientesCoordinacion;
